/// <reference types="applepayjs" />
import { Frequency } from '../transaction-store/recurring';

/**
 * Translates an app6 frequency into the interval Apple renders on the payment
 * sheet, and assembles the `recurringPaymentRequest` that makes Apple issue a
 * merchant token (MPAN) instead of a single-use device token.
 *
 * The interval is descriptive only; gmsnext owns the actual schedule.
 */

export interface ApplePayRecurringInterval {
  unit: ApplePayJS.ApplePayRecurringPaymentDateUnit;
  count: number;
}

/**
 * `null` means Apple Pay cannot honestly express the cadence and must be
 * withheld for that frequency.
 *
 * - `Once` is not recurring; a one-time gift must carry no
 *   `recurringPaymentRequest` so the sheet stays on the lower API version.
 * - `1st and 15th` is semi-monthly, not a fixed interval. "Every 15 days"
 *   would state a cadence on the sheet that we do not bill.
 */
export function getApplePayRecurringInterval(
  frequency: Frequency | string
): ApplePayRecurringInterval | null {
  switch (frequency) {
    case Frequency.Weekly:
      return { unit: 'day', count: 7 };
    case Frequency.BiWeekly:
      return { unit: 'day', count: 14 };
    case Frequency.Monthly:
      return { unit: 'month', count: 1 };
    case Frequency.Quarterly:
      return { unit: 'month', count: 3 };
    case Frequency.SemiAnnually:
      return { unit: 'month', count: 6 };
    case Frequency.Annually:
      return { unit: 'year', count: 1 };
    case Frequency.TwiceMonthly:
    case Frequency.Once:
    default:
      return null;
  }
}

/** Whether Apple Pay can be offered for this recurring frequency. */
export function isApplePayRecurringFrequency(
  frequency: Frequency | string
): boolean {
  return getApplePayRecurringInterval(frequency) !== null;
}

export function buildApplePayRecurringPaymentRequest({
  frequency,
  frequencyLabel,
  amount,
  organizationLabel,
  managementURL,
  endDate
}: {
  frequency: Frequency | string;
  frequencyLabel: string;
  /** Fee-inclusive; must equal the sheet total or the donor authorizes the wrong figure. */
  amount: string;
  organizationLabel: string;
  managementURL: string;
  endDate?: Date | string;
}): ApplePayJS.ApplePayRecurringPaymentRequest | null {
  const interval = getApplePayRecurringInterval(frequency);
  // Apple requires managementURL on a recurring request; without one the
  // request is dropped and a device token comes back, so refuse to build a
  // half-request rather than silently degrade.
  if (!interval || !managementURL) {
    return null;
  }

  const description = `${frequencyLabel} donation to ${organizationLabel}`;
  const regularBilling: ApplePayJS.ApplePayLineItem = {
    label: description,
    amount,
    // Apple raises an error when regularBilling omits this.
    paymentTiming: 'recurring',
    recurringPaymentIntervalUnit: interval.unit,
    recurringPaymentIntervalCount: interval.count
    // recurringPaymentStartDate is deliberately omitted: Apple treats its
    // absence as "the first payment is part of this transaction", which is
    // how iDonate bills the opening gift.
  };
  if (endDate) {
    regularBilling.recurringPaymentEndDate = new Date(endDate);
  }

  return {
    paymentDescription: description,
    managementURL,
    regularBilling
  };
}

/**
 * The page Apple sends the donor to when they tap "manage" on the recurring
 * payment. Donor self-serve cancels schedules and swaps payment methods.
 * `donor_portal_url` is a bare host (see thank-you.component's viewDonations),
 * branded portals nest the org slug.
 */
export function buildDonorManagementUrl(
  portalHost: string,
  organizationSlug: string,
  isBranded: boolean
): string {
  if (!portalHost) {
    return '';
  }
  const base = `https://${portalHost.replace(/^https?:\/\//, '').replace(/\/+$/, '')}`;
  return isBranded ? `${base}/${organizationSlug}/recurring` : `${base}/recurring`;
}
